import { useInfiniteQuery } from '@tanstack/react-query';
import { clsx } from 'clsx';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { ProviderList } from '../../../../../js/api';
import { ProvidersService, SourceTypeEnum } from '../../../../../js/api';
import { SessionContext } from '../../../../../js/app/contexts/SessionContext';
import { useDocumentTitle } from '../../../../../js/app/hooks/use-document-title';
import { Badge } from '../../../components/badge';
import { Drawer, DrawerBody } from '../../../components/drawer';
import { Heading } from '../../../components/heading';
import { ListErrorState } from '../../../components/list-error-state';
import { BooleanFilterConfig, DateFilterConfig, ListFilter } from '../../../components/list-filter';
import { ReferrerBackLink } from '../../../components/referrer-back-link';
import { StatusPill } from '../../../components/StatusPill';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableSortHeader,
} from '../../../components/table';
import { WorkflowScopes } from '../../../constants/workflows';
import { useCodeDefinitionsAsMaps } from '../../../hooks/useCodeDefinitions';
import { useInfiniteScroll } from '../../../hooks/useInfiniteScroll';
import { useListFilterParams } from '../../../hooks/useListFilterParams';
import { SOURCE_TYPE_ORGANIZATION_BADGE_STYLE } from '../../../utils/dataStyles';
import { localDayEndIso, localDayStartIso } from '../../../utils/date';
import { STICKY_HEADER } from '../../../utils/globalStyles';
import {
  MAIN_CONTENT_AREA,
  MAIN_CONTENT_AREA_FIXED,
  PAGE_HEADER,
  TABLE_SCROLL_BOX,
} from '../../../utils/layoutStyles';
import { TRANSITION_ALL } from '../../../utils/transitionStyles';
import { formatAdminRatePercent } from '../../../utils/utils';
import { ProviderAdminLink } from './ProviderAdminLink';
import {
  ProviderCard,
  ProviderCardMaps,
  StepFlag,
  formatMarketplaceFee,
  formatProviderTimestamp,
  labelsFor,
} from './ProviderCard';
import { ProviderDrawer } from './ProviderDrawer';
import { STAFF_PROVIDERS_LIST_QUERY_KEY } from './queryKeys';

const PAGE_SIZE = 20;
type ApiParams = Parameters<typeof ProvidersService.providersList>[0];

/** CodeDefinition category → API query param (ListFilter renders one multi-select per key). */
const CATEGORY_TO_API_PARAM: Record<string, keyof ApiParams> = {
  RegistrationStatus: 'registrationStatus',
  ProfileDisposition: 'profileDisposition',
  SourceType: 'sourceType',
  Specialty: 'specialties',
};
const CATEGORIES = Object.keys(CATEGORY_TO_API_PARAM);
const LABEL_OVERRIDES: Record<string, string> = {
  RegistrationStatus: 'Registration',
  ProfileDisposition: 'Disposition',
  SourceType: 'Source',
  Specialty: 'Specialties',
  State: 'Home State',
};
/** Checkbox filters: unchecked sends nothing (everyone), checked narrows to the flag. */
// From / to date pills; the keys are the API's range filters (`created_at_after`, ...).
const DATE_FILTERS: DateFilterConfig[] = [
  { key: 'created_at', label: 'Created' },
  { key: 'updated_at', label: 'Updated' },
];
const BOOLEAN_FILTERS: BooleanFilterConfig[] = [
  {
    queryString: 'has_imlc',
    trueLabel: 'Has IMLC',
    falseLabel: '',
    renderAs: 'checkbox',
    defaultValue: false,
  },
];
const CODE_DEF_CATEGORIES = [
  'Degree',
  'Specialty',
  'ProfileDisposition',
  'RegistrationStatus',
  'SourceType',
  'ScheduleType',
];

/** Every column is sortable; the same list drives the table headers and the card-view sort dropdown. */
const COLUMNS: Array<{ label: string; sortKey: string; sortLabel?: string }> = [
  { label: 'Clinician Name', sortKey: 'full_name' },
  { label: 'Public Name', sortKey: 'public_name' },
  { label: 'Degree', sortKey: 'degree' },
  { label: 'Specialties', sortKey: 'specialties' },
  { label: 'Home State', sortKey: 'state' },
  { label: 'IMLC', sortKey: 'has_imlc' },
  { label: 'Admin Rate', sortKey: 'admin_rate' },
  { label: 'Mkt Fee', sortKey: 'marketplace_hourly_fee', sortLabel: 'Marketplace Fee' },
  { label: 'Phone', sortKey: 'user__phone_number' },
  { label: 'Texting', sortKey: 'user__texting_number' },
  { label: 'Registration', sortKey: 'user__registration_status' },
  { label: 'Source', sortKey: 'source_type' },
  { label: 'Disposition', sortKey: 'profile_disposition' },
  { label: 'Accepting', sortKey: 'accepting_connections' },
  { label: 'Schedule', sortKey: 'schedule_type' },
  { label: 'Status', sortKey: 'current_status__title' },
  { label: 'Basics', sortKey: 'has_completed_basic_info', sortLabel: 'Basics Done' },
  { label: 'Rates', sortKey: 'has_completed_rates', sortLabel: 'Rates Done' },
  { label: 'Traits', sortKey: 'has_completed_professional_traits', sortLabel: 'Traits Done' },
  { label: 'Updated', sortKey: 'updated_at', sortLabel: 'Last Updated' },
  { label: 'Created', sortKey: 'created_at' },
];
const SORT_OPTIONS = COLUMNS.map((c) => ({ label: c.sortLabel ?? c.label, value: c.sortKey }));

const labelOrCode = (code: string | null | undefined, map: Record<string, string>) =>
  code ? map[code] || code : '--';

/** Table cell renderer per column sortKey. Keyed so every COLUMNS entry has a rendered home. */
const CELL_RENDERERS: Record<
  string,
  (provider: ProviderList, maps: ProviderCardMaps) => React.ReactNode
> = {
  // Staff always see the true name, so the viewer-masked display_name adds
  // nothing here (user feedback 2026-09-08); the serializer still emits it
  // for the future HCO consumers.
  full_name: (p) => (
    <span className="tw:inline-flex tw:items-center tw:gap-1">
      {p.full_name}
      {p.source_type === SourceTypeEnum.ORGANIZATION && (
        <span className={SOURCE_TYPE_ORGANIZATION_BADGE_STYLE}>1099</span>
      )}
    </span>
  ),
  public_name: (p) => p.public_name,
  degree: (p, maps) => labelsFor(p.degree, maps.degree),
  // Multi-specialty providers would otherwise stretch the row; clip at ~30
  // characters and keep the full list on hover.
  specialties: (p, maps) => {
    const label = labelsFor(p.specialties, maps.specialty);
    return (
      <span className="tw:block tw:max-w-[30ch] tw:truncate" title={label}>
        {label}
      </span>
    );
  },
  state: (p) => p.state || '--',
  // Green badge for holders, blank otherwise ('' — not null — so renderCell adds no '--').
  has_imlc: (p) =>
    p.has_imlc ? (
      <Badge color="green" title="Has IMLC" data-testid={`provider-imlc-${p.id}`}>
        <i className="fas fa-check" aria-hidden="true" />
        IMLC
      </Badge>
    ) : (
      ''
    ),
  admin_rate: (p) => formatAdminRatePercent(p.admin_rate),
  marketplace_hourly_fee: (p) => formatMarketplaceFee(p.marketplace_hourly_fee),
  user__phone_number: (p) => p.phone_number || '--',
  user__texting_number: (p) => p.texting_number || '--',
  user__registration_status: (p, maps) => labelOrCode(p.registration_status, maps.registration),
  source_type: (p, maps) => labelOrCode(p.source_type, maps.source),
  profile_disposition: (p, maps) => labelOrCode(p.profile_disposition, maps.disposition),
  accepting_connections: (p) => (p.accepting_connections ? 'Yes' : 'No'),
  schedule_type: (p, maps) => labelOrCode(p.schedule_type, maps.schedule),
  current_status__title: (p) => (
    <StatusPill scope={WorkflowScopes.Provider} status={p.current_status} small />
  ),
  has_completed_basic_info: (p) => <StepFlag done={p.has_completed_basic_info} label="Basics" />,
  has_completed_rates: (p) => <StepFlag done={p.has_completed_rates} label="Rates" />,
  has_completed_professional_traits: (p) => (
    <StepFlag done={p.has_completed_professional_traits} label="Traits" />
  ),
  updated_at: (p) => formatProviderTimestamp(p.updated_at),
  created_at: (p) => formatProviderTimestamp(p.created_at),
};

const renderCell = (provider: ProviderList, sortKey: string, maps: ProviderCardMaps) =>
  CELL_RENDERERS[sortKey]?.(provider, maps) ?? '--';

// eslint-disable-next-line sonarjs/cognitive-complexity
export const StaffProvidersList: React.FC = () => {
  useDocumentTitle('Clinicians');
  const { user } = useContext(SessionContext);
  // Django admin flag (not app-level staff): gates the per-row admin link only.
  const showAdminLink = Boolean(user?.is_staff);

  const [searchParams, setSearchParams] = useSearchParams();
  const { filterValues, updateFilters } = useListFilterParams(searchParams, setSearchParams, {
    categories: CATEGORIES,
    booleanFilters: BOOLEAN_FILTERS,
    dateFilters: DATE_FILTERS,
    displayTypePrefKey: 'StaffProviders',
  });

  // The selected provider lives in `?id=` so the drawer survives reloads and
  // can be deep-linked (same convention as the clearance/connection lists).
  const selectedIdFromUrl = searchParams.get('id');
  const [isDrawerOpen, setIsDrawerOpen] = useState(Boolean(selectedIdFromUrl));
  useEffect(() => {
    setIsDrawerOpen(Boolean(selectedIdFromUrl));
  }, [selectedIdFromUrl]);

  const handleRowClick = useCallback(
    (providerId: string) => {
      const next = new URLSearchParams(searchParams);
      next.set('id', providerId);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const handleCloseDrawer = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete('id');
    // Row clicks keep `tab` so it sticks across providers; closing resets it.
    next.delete('tab');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const search = filterValues.search || '';
  const statuses = useMemo(() => filterValues.statuses || [], [filterValues.statuses]);
  const states = useMemo(() => filterValues.states || [], [filterValues.states]);
  const categoryFilters = useMemo(() => filterValues.categories || {}, [filterValues.categories]);
  const hasImlc = filterValues.booleanFilters?.has_imlc === true;
  const displayType = (filterValues.displayType || 'table') as 'card' | 'table';
  // Table view: the rows scroll in their own box under a sticky header (utils/layoutStyles).
  const tableScrolls = displayType !== 'card';
  const ordering = (filterValues.ordering || '-updated_at') as string;

  const setOrdering = useCallback(
    (next: string) => updateFilters({ ...filterValues, ordering: next }),
    [updateFilters, filterValues]
  );

  // {registrationStatus: [...], sourceType: [...], ...} — only the categories with a selection.
  const categoryApiParams = useMemo(() => {
    const params: Record<string, string[]> = {};
    Object.entries(categoryFilters).forEach(([category, values]) => {
      const apiParam = CATEGORY_TO_API_PARAM[category];
      if (apiParam && values && values.length > 0) {
        params[apiParam] = values;
      }
    });
    return params as Partial<ApiParams>;
  }, [categoryFilters]);

  // The picked days as the instants that start / end them in the viewer's timezone.
  const createdRange = filterValues.dateFilters?.created_at;
  const updatedRange = filterValues.dateFilters?.updated_at;
  const dateRangeParams = useMemo(
    () => ({
      createdAtAfter: localDayStartIso(createdRange?.from),
      createdAtBefore: localDayEndIso(createdRange?.to),
      updatedAtAfter: localDayStartIso(updatedRange?.from),
      updatedAtBefore: localDayEndIso(updatedRange?.to),
    }),
    [createdRange?.from, createdRange?.to, updatedRange?.from, updatedRange?.to]
  );

  const queryKey = useMemo(
    () => [
      STAFF_PROVIDERS_LIST_QUERY_KEY,
      search,
      statuses,
      states,
      categoryFilters,
      hasImlc,
      dateRangeParams,
      ordering,
    ],
    [search, statuses, states, categoryFilters, hasImlc, dateRangeParams, ordering]
  );

  const { data, isLoading, isError, isFetchingNextPage, hasNextPage, fetchNextPage } =
    useInfiniteQuery({
      queryKey,
      queryFn: async ({ pageParam = 0 }) =>
        ProvidersService.providersList({
          search: search || undefined,
          status: statuses.length > 0 ? statuses : undefined,
          state: states.length > 0 ? (states as NonNullable<ApiParams['state']>) : undefined,
          ...categoryApiParams,
          hasImlc: hasImlc || undefined,
          ...dateRangeParams,
          ordering,
          limit: PAGE_SIZE,
          offset: pageParam as number,
        }),
      getNextPageParam: (lastPage, allPages) =>
        lastPage.next ? allPages.length * PAGE_SIZE : undefined,
    });

  const providers = useMemo(() => data?.pages.flatMap((page) => page.results || []) || [], [data]);
  const totalCount = data?.pages[0]?.count || 0;

  const loadMoreProviders = useCallback(async () => {
    if (hasNextPage && !isFetchingNextPage) {
      await fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const { loadingRef } = useInfiniteScroll(loadMoreProviders, hasNextPage || false, {
    enabled: hasNextPage && !isLoading,
  });

  const { data: codeDefinitionMaps } = useCodeDefinitionsAsMaps(CODE_DEF_CATEGORIES);
  const maps: ProviderCardMaps = useMemo(
    () => ({
      degree: codeDefinitionMaps?.Degree || {},
      specialty: codeDefinitionMaps?.Specialty || {},
      disposition: codeDefinitionMaps?.ProfileDisposition || {},
      registration: codeDefinitionMaps?.RegistrationStatus || {},
      source: codeDefinitionMaps?.SourceType || {},
      schedule: codeDefinitionMaps?.ScheduleType || {},
    }),
    [codeDefinitionMaps]
  );

  // Hoisted out of the JSX so the error/loading/empty branches don't deepen the
  // ternary nesting around the rows (sonar cognitive-complexity budget).
  const listStateContent = isError ? (
    <ListErrorState testId="providers-error" />
  ) : isLoading && providers.length === 0 ? (
    <div className="tw:text-center tw:py-8" data-testid="providers-loading">
      Loading clinicians...
    </div>
  ) : providers.length === 0 ? (
    <div className="tw:text-center tw:text-zinc-500 tw:py-8" data-testid="providers-empty">
      No clinicians found.
      <div className="tw:text-zinc-500 tw:italic tw:text-sm">Refine your search or filters.</div>
    </div>
  ) : null;

  return (
    <div className="tw:flex tw:h-[calc(100vh-72px)] tw:overflow-hidden">
      <div
        className={clsx(
          `tw:flex tw:flex-col ${TRANSITION_ALL} tw:overflow-hidden`,
          isDrawerOpen ? 'tw:w-3/5 tw:lg:w-3/5' : 'tw:w-full'
        )}
      >
        <main
          className={clsx(MAIN_CONTENT_AREA, tableScrolls && MAIN_CONTENT_AREA_FIXED)}
          data-testid="staff-providers-page"
        >
          <div className={STICKY_HEADER}>
            <ReferrerBackLink />
            <div className={PAGE_HEADER}>
              <Heading level={1} className="tw:!text-brand-green-0">
                <i className="far fa-fw fa-stethoscope tw:mr-1" />
                Clinicians
              </Heading>
            </div>
            <div className="tw:mt-1">
              <ListFilter
                show_search={true}
                workflow_scope={WorkflowScopes.Provider}
                showStatus={true}
                showReason={false}
                showStateFilter={true}
                categories={CATEGORIES}
                labelOverrides={LABEL_OVERRIDES}
                booleanFilters={BOOLEAN_FILTERS}
                dateFilters={DATE_FILTERS}
                showDisplayType={true}
                sortOptions={SORT_OPTIONS}
                values={filterValues}
                onFilterChange={updateFilters}
              />
            </div>
          </div>
          <div className={clsx('tw:mt-3', tableScrolls && TABLE_SCROLL_BOX)}>
            {listStateContent ?? (
              <>
                <div className="tw:text-right tw:text-xs tw:text-zinc-500 tw:my-1 tw:mr-1">
                  Showing {providers.length} of {totalCount} clinicians
                </div>

                {displayType === 'card' ? (
                  <div className="tw:@container">
                    <div className="tw:grid tw:grid-cols-1 tw:gap-3 tw:@min-[513px]:grid-cols-2 tw:@min-[793px]:grid-cols-3">
                      {providers.map((provider) => (
                        <button
                          key={provider.id}
                          type="button"
                          onClick={() => handleRowClick(provider.id)}
                          className="tw:block tw:w-full tw:text-left"
                          data-testid={`provider-card-${provider.id}`}
                        >
                          <ProviderCard
                            provider={provider}
                            maps={maps}
                            isSelected={provider.id === selectedIdFromUrl}
                            adminLink={
                              showAdminLink ? <ProviderAdminLink providerId={provider.id} /> : null
                            }
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <Table striped dense stickyHeader={tableScrolls}>
                    <TableHead>
                      {showAdminLink && (
                        <TableHeader className="tw:w-8 tw:relative">
                          {/* tw:relative keeps the sr-only (absolute) label inside its cell */}
                          <span className="tw:sr-only">Django admin</span>
                        </TableHeader>
                      )}
                      {COLUMNS.map((column) => (
                        <TableSortHeader
                          key={column.sortKey}
                          sortKey={column.sortKey}
                          ordering={ordering}
                          onOrderingChange={setOrdering}
                        >
                          {column.label}
                        </TableSortHeader>
                      ))}
                    </TableHead>
                    <TableBody>
                      {providers.map((provider) => (
                        <TableRow
                          key={provider.id}
                          onClick={() => handleRowClick(provider.id)}
                          className={clsx(
                            provider.id === selectedIdFromUrl && 'tw:!bg-brand-green-2'
                          )}
                          data-testid={`provider-row-${provider.id}`}
                        >
                          {showAdminLink && (
                            <TableCell>
                              <ProviderAdminLink providerId={provider.id} />
                            </TableCell>
                          )}
                          {COLUMNS.map((column) => (
                            <TableCell key={column.sortKey} className="tw:whitespace-nowrap">
                              {renderCell(provider, column.sortKey, maps)}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}

                {hasNextPage && (
                  <div ref={loadingRef} className="tw:flex tw:justify-center tw:py-8">
                    {isFetchingNextPage && (
                      <div className="tw:text-center tw:text-zinc-500">
                        Loading more clinicians...
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </main>
      </div>

      <Drawer open={isDrawerOpen} onClose={handleCloseDrawer} width="3xl" mode="side-by-side">
        <DrawerBody className="tw:!text-sm/6">
          {selectedIdFromUrl && (
            <ProviderDrawer
              key={selectedIdFromUrl}
              providerId={selectedIdFromUrl}
              onClose={handleCloseDrawer}
            />
          )}
        </DrawerBody>
      </Drawer>
    </div>
  );
};
